<template>
    <vx-modal v-model:visible="visible" size="md" padding="standard">
        <template v-slot:header>{{ title[props.mode] }}</template>

        <div class="flex flex-col gap-4 text-sm text-slate-800">
            <div v-if="props.mode === 'regenerate'" data-test="regenerate-warning">
                The current token stops working as soon as the new one is generated. Paste the new token into your
                identity provider right away.
            </div>
            <div v-else-if="props.mode === 'extend'" data-test="extend-hint">
                The token itself does not change; only its expiry moves.
            </div>

            <div>
                <vx-label :id="expiresInDaysLabelId" size="md">
                    {{ props.mode === 'extend' ? 'Expires in' : 'Valid for' }}
                </vx-label>
                <vx-radio-selection
                    v-model="expiresInDays"
                    :aria-labelledby="expiresInDaysLabelId"
                    data-test="expires-in-days"
                >
                    <vx-radio-selection-item
                        v-for="option in SCIM_TOKEN_EXPIRY_OPTIONS"
                        :key="option.value"
                        :value="String(option.value)"
                        :data-test="`expires-in-days-${option.value}`"
                    >
                        <template v-slot:header>{{ option.label }}</template>
                    </vx-radio-selection-item>
                </vx-radio-selection>
                <div class="mt-2 text-xs text-slate-500">
                    You are emailed 14 days before the token expires and again when it has expired.
                </div>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="visible = false"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="confirm()"
                    data-test="confirm"
                    :loading="submitting"
                    type="button"
                    class="grow"
                    size="lg"
                    :color="props.mode === 'regenerate' ? 'danger' : 'primary'"
                >
                    {{ title[props.mode] }}
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { VxButton, VxLabel, VxModal, VxRadioSelection, VxRadioSelectionItem } from '@voxie/frontend-components';
import { ref, useId, watch } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { ssoClient } from '~/services';
import type { SsoScim } from '~/services/ssoClient.types';
import { catchRequestError } from '~/utils/request';
import { SCIM_TOKEN_DAYS_DEFAULT, SCIM_TOKEN_EXPIRY_OPTIONS } from './ssoSettingsHelpers';

export type ScimTokenModalMode = 'generate' | 'regenerate' | 'extend';

const props = defineProps<{ teamId: number; mode: ScimTokenModalMode }>();
const emit = defineEmits<{ generated: [token: string, scim: SsoScim]; extended: [scim: SsoScim] }>();
const visible = defineModel<boolean>('visible', { default: false });

const toasted = useToasted();

const title: Record<ScimTokenModalMode, string> = {
    generate: 'Generate token',
    regenerate: 'Regenerate token',
    extend: 'Extend token',
};

// The radio selection models a string.
const expiresInDays = ref(String(SCIM_TOKEN_DAYS_DEFAULT));
const submitting = ref(false);

const expiresInDaysLabelId = useId();

watch(visible, (open) => {
    if (open) {
        expiresInDays.value = String(SCIM_TOKEN_DAYS_DEFAULT);
    }
});

const confirm = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;

    const payload = { expires_in_days: Number(expiresInDays.value) };

    try {
        if (props.mode === 'extend') {
            const { data } = await ssoClient.updateScimToken(props.teamId, payload);
            emit('extended', data);
            toasted.success('Token expiry extended.');
        } else {
            const { data } = await ssoClient.generateScimToken(props.teamId, payload);
            emit('generated', data.token, data.scim);
            toasted.success('Token generated. Copy it now; it is shown only once.');
        }

        visible.value = false;
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        submitting.value = false;
    }
};
</script>
